import React, { useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import ProductExportModal from './ProductExportModal';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SxProps } from '@mui/system';
import {
  BeaconProTable,
  Column
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProTable/BeaconProTable';
import {
  ChartDataAdapter,
  Product,
  TableGridData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { PAGINATION_CONSTANTS } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/constants';
import { ContentSubTabs } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';

interface ContentTableGridProps {
  title: string | React.ReactNode;
  queryFn: (page: number) => Promise<ChartDataAdapter<TableGridData>>;
  columnDefinitions: Column<any>[];
  queryKeyOverride?: string;
  productColumn?: Column<{ stacklineSku: string; product: Product }>;
  mode?: 'table' | 'grid' | 'both';
  tableSx?: SxProps;
  subtitle?: string | React.ReactNode;
  filterModal?: React.ReactNode;
  setOpen?: () => void;
  disableFilterButton?: boolean;
  loadingCallback?: (isLoading: boolean) => void;
  forceLoading?: boolean;
  isFilterActive?: boolean;
  pageSize?: number;
  exportType: ContentSubTabs;
}

export const ContentTableGridV3 = ({
  title,
  columnDefinitions,
  queryFn,
  queryKeyOverride,
  productColumn = null,
  tableSx,
  subtitle,
  filterModal,
  setOpen,
  isFilterActive = false,
  disableFilterButton = false,
  loadingCallback,
  forceLoading,
  pageSize = PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
  exportType
}: ContentTableGridProps) => {
  const [pageIndex, setPageIndex] = useState(1);

  const name =
    queryKeyOverride ??
    `content-table-grid-${typeof title === 'string' ? title.toLowerCase().replace(/\s+/g, '-') : 'custom'}`;
  const columns = productColumn ? [productColumn, ...columnDefinitions] : columnDefinitions;
  const [openExportModal, setOpenExportModal] = useState(false);

  const wrappedQueryFn = () => {
    return queryFn(pageIndex - 1);
  };

  return (
    <BeaconProChart
      queryFn={wrappedQueryFn}
      name={`${name}-page-${pageIndex}`}
      loadingCallback={loadingCallback}
      forceLoading={forceLoading}
      pageSize={pageSize}
    >
      <ProductExportModal open={openExportModal} onClose={() => setOpenExportModal(false)} exportType={exportType} />
      {filterModal}
      <BeaconProChart.Header
        menuButton={() => (
          <Flex gap={16}>
            <SlButton
              active={isFilterActive}
              onClick={setOpen}
              disabled={disableFilterButton}
              buttonSx={{ width: '73px', paddingLeft: '14px', paddingRight: '14px' }}
            >
              Filter
            </SlButton>
            <SlButton
              onClick={() => {
                setOpenExportModal(true);
              }}
            >
              Export
            </SlButton>
          </Flex>
        )}
      >
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle>{subtitle}</BeaconProChart.Subtitle>
      <BeaconProChart.Visualization
        deferLoading
        render={(adapter: ChartDataAdapter<TableGridData>, isLoading) => {
          const data = adapter?.transform();
          const hasMorePages = adapter?.hasNextPage || false;
          const showPagination = !isLoading && (hasMorePages || pageIndex > 1);

          const handlePageChange = (page: number) => {
            if (page > 0 && (page < pageIndex || hasMorePages)) {
              setPageIndex(page);
            }
          };

          return (
            <Flex sx={{ marginTop: '32px' }} flexDirection="column" alignItems="flex-start">
              <BeaconProTable
                isLoading={isLoading}
                data={data ?? []}
                page={1}
                columns={columns}
                tableSx={tableSx}
                itemsPerPage={pageSize}
              />
              {showPagination && (
                <div style={{ marginTop: '24px', alignSelf: 'flex-end' }}>
                  <Pagination
                    currentPage={pageIndex}
                    onClickNext={() => handlePageChange(pageIndex + 1)}
                    onClickPrevious={() => handlePageChange(pageIndex - 1)}
                    shouldUseInfinitePagination
                  />
                </div>
              )}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
